/**
 * Trade Analysis Page
 * Trade history, cluster analysis, trade details
 */

import React, { useEffect, useState } from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import '../styles/validation.css';

const API_BASE = 'https://trading-api-1075463475276.us-central1.run.app/api/trading-validation';

interface Trade {
  trade_id: string;
  symbol: string;
  entry_price: number;
  exit_price: number;
  entry_time: string;
  exit_time: string;
  pnl: number;
  pnl_pct: number;
  duration_minutes: number;
  win: boolean;
  trade_type: string;
  enhancement_context: {
    correlation_check: string;
    regime: string;
    volatility_multiplier: number;
    ensemble_agreement: number;
  };
}

interface Cluster {
  cluster_id: string;
  trade_type: string;
  symbol: string;
  count: number;
  avg_pnl: number;
  win_rate: number;
  profitability: string;
}

const COLORS = ['#4CAF50', '#FF9800', '#F44336', '#2196F3', '#9C27B0', '#00BCD4'];

const TradeAnalysis: React.FC = () => {
  const [trades, setTrades] = useState<Trade[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [selectedTrade, setSelectedTrade] = useState<Trade | null>(null);
  const [sortBy, setSortBy] = useState<'pnl' | 'date' | 'symbol'>('date');
  const [filterSymbol, setFilterSymbol] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tradeRes, clusterRes] = await Promise.all([
          fetch(`${API_BASE}/trade-history`),
          fetch(`${API_BASE}/cluster-analysis`),
        ]);

        const tradeData = await tradeRes.json();
        const clusterData = await clusterRes.json();

        setTrades(tradeData.data || []);
        setClusters(clusterData.data || []);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching trade data:', error);
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 300000); // 5 min
    return () => clearInterval(interval);
  }, []);

  if (loading) return <div className="validation-container"><div className="loading">Loading trade analysis...</div></div>;

  // Filter and sort trades
  let filteredTrades = trades;
  if (filterSymbol) {
    filteredTrades = trades.filter(t => t.symbol.toUpperCase().includes(filterSymbol.toUpperCase()));
  }

  if (sortBy === 'pnl') {
    filteredTrades = [...filteredTrades].sort((a, b) => b.pnl - a.pnl);
  } else if (sortBy === 'symbol') {
    filteredTrades = [...filteredTrades].sort((a, b) => a.symbol.localeCompare(b.symbol));
  }

  // Group trades by symbol for pie chart
  const tradesBySymbol = trades.reduce((acc, t) => {
    const existing = acc.find(x => x.name === t.symbol);
    if (existing) {
      existing.value += 1;
    } else {
      acc.push({ name: t.symbol, value: 1 });
    }
    return acc;
  }, [] as Array<{name: string; value: number}>);

  // Win/Loss by symbol
  const symbolStats = trades.reduce((acc, t) => {
    const existing = acc.find(x => x.symbol === t.symbol);
    if (existing) {
      if (t.win) {
        existing.wins += 1;
      } else {
        existing.losses += 1;
      }
    } else {
      acc.push({
        symbol: t.symbol,
        wins: t.win ? 1 : 0,
        losses: t.win ? 0 : 1,
      });
    }
    return acc;
  }, [] as Array<{symbol: string; wins: number; losses: number}>);

  return (
    <div className="validation-container">
      <div className="validation-header">
        <h1>Trade Analysis</h1>
        <p className="subtitle">Trade history, cluster profitability, detailed analysis</p>
      </div>

      {/* Trade Filters */}
      <div className="trade-filters">
        <div className="filter-group">
          <label>Symbol:</label>
          <input
            type="text"
            placeholder="Filter by symbol..."
            value={filterSymbol}
            onChange={(e) => setFilterSymbol(e.target.value)}
            className="filter-input"
          />
        </div>
        <div className="filter-group">
          <label>Sort by:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)} className="filter-select">
            <option value="date">Date (Latest First)</option>
            <option value="pnl">P&L (Highest First)</option>
            <option value="symbol">Symbol (A-Z)</option>
          </select>
        </div>
        <div className="filter-group">
          <span className="result-count">Showing {filteredTrades.length} of {trades.length} trades</span>
        </div>
      </div>

      {/* Symbol Distribution */}
      <div className="charts-grid-full">
        <div className="widget chart-widget">
          <h3>Trades by Symbol</h3>
          {tradesBySymbol.length > 0 && (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={tradesBySymbol}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({name, value}) => `${name} (${value})`}
                  outerRadius={80}
                  fill="#2196F3"
                  dataKey="value"
                >
                  {tradesBySymbol.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="widget chart-widget">
          <h3>Win/Loss by Symbol</h3>
          {symbolStats.length > 0 && (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={symbolStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="symbol" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="wins" fill="#4CAF50" name="Wins" />
                <Bar dataKey="losses" fill="#F44336" name="Losses" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Cluster Analysis */}
      {clusters.length > 0 && (
        <div className="widget">
          <h3>Cluster Profitability Analysis</h3>
          <div className="clusters-grid">
            {clusters.map((cluster) => (
              <div key={cluster.cluster_id} className={`cluster-card ${cluster.profitability === 'HIGH' ? 'profitable' : cluster.profitability === 'MEDIUM' ? 'neutral' : 'unprofitable'}`}>
                <div className="cluster-header">
                  <div className="cluster-title">{cluster.trade_type} - {cluster.symbol}</div>
                  <div className={`cluster-badge ${cluster.profitability.toLowerCase()}`}>
                    {cluster.profitability}
                  </div>
                </div>
                <div className="cluster-details">
                  <div className="cluster-stat">
                    <span className="label">Trades:</span>
                    <span className="value">{cluster.count}</span>
                  </div>
                  <div className="cluster-stat">
                    <span className="label">Avg P&L:</span>
                    <span className="value" style={{ color: cluster.avg_pnl > 0 ? '#4CAF50' : '#F44336' }}>
                      ${cluster.avg_pnl.toFixed(2)}
                    </span>
                  </div>
                  <div className="cluster-stat">
                    <span className="label">Win Rate:</span>
                    <span className="value">{(cluster.win_rate * 100).toFixed(0)}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Trades Table */}
      <div className="widget">
        <h3>Trade History (Latest 50)</h3>
        <div className="trades-table-wrapper">
          <table className="trades-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Symbol</th>
                <th>Type</th>
                <th>Entry</th>
                <th>Exit</th>
                <th>P&L</th>
                <th>Duration</th>
                <th>Regime</th>
                <th>Ensemble</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredTrades.slice(0, 50).map((trade) => (
                <tr key={trade.trade_id} className={trade.win ? 'win' : 'loss'}>
                  <td className="time-cell">
                    {new Date(trade.entry_time).toLocaleTimeString()}
                  </td>
                  <td className="symbol-cell">
                    <strong>{trade.symbol}</strong>
                  </td>
                  <td className="type-cell">
                    <span className={`type-badge ${trade.trade_type.toLowerCase()}`}>
                      {trade.trade_type}
                    </span>
                  </td>
                  <td className="price-cell">${trade.entry_price.toFixed(2)}</td>
                  <td className="price-cell">${trade.exit_price.toFixed(2)}</td>
                  <td className="pnl-cell">
                    <span style={{ color: trade.pnl > 0 ? '#4CAF50' : '#F44336' }}>
                      ${trade.pnl.toFixed(2)} ({trade.pnl_pct.toFixed(2)}%)
                    </span>
                  </td>
                  <td className="duration-cell">{trade.duration_minutes}m</td>
                  <td className="regime-cell">{trade.enhancement_context.regime}</td>
                  <td className="ensemble-cell">
                    <div className="ensemble-badge" style={{ backgroundColor: trade.enhancement_context.ensemble_agreement > 0.75 ? '#4CAF50' : '#FF9800' }}>
                      {(trade.enhancement_context.ensemble_agreement * 100).toFixed(0)}%
                    </div>
                  </td>
                  <td className="action-cell">
                    <button
                      className="view-btn"
                      onClick={() => setSelectedTrade(trade)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Trade Details Modal */}
      {selectedTrade && (
        <div className="modal-overlay" onClick={() => setSelectedTrade(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Trade Details - {selectedTrade.symbol}</h2>
              <button className="modal-close" onClick={() => setSelectedTrade(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <span className="label">Trade ID:</span>
                <span className="value">{selectedTrade.trade_id}</span>
              </div>
              <div className="detail-row">
                <span className="label">Type:</span>
                <span className="value">{selectedTrade.trade_type}</span>
              </div>
              <div className="detail-row">
                <span className="label">Entry Time:</span>
                <span className="value">{new Date(selectedTrade.entry_time).toLocaleString()}</span>
              </div>
              <div className="detail-row">
                <span className="label">Exit Time:</span>
                <span className="value">{new Date(selectedTrade.exit_time).toLocaleString()}</span>
              </div>
              <div className="detail-row">
                <span className="label">Entry Price:</span>
                <span className="value">${selectedTrade.entry_price.toFixed(2)}</span>
              </div>
              <div className="detail-row">
                <span className="label">Exit Price:</span>
                <span className="value">${selectedTrade.exit_price.toFixed(2)}</span>
              </div>
              <div className="detail-row highlight">
                <span className="label">P&L:</span>
                <span className="value" style={{ color: selectedTrade.pnl > 0 ? '#4CAF50' : '#F44336', fontWeight: 'bold' }}>
                  ${selectedTrade.pnl.toFixed(2)} ({selectedTrade.pnl_pct.toFixed(2)}%)
                </span>
              </div>
              <div className="detail-row">
                <span className="label">Duration:</span>
                <span className="value">{selectedTrade.duration_minutes} minutes</span>
              </div>
              <div className="enhancement-details">
                <h4>Enhancement Context</h4>
                <div className="detail-row">
                  <span className="label">Correlation Check:</span>
                  <span className="value">{selectedTrade.enhancement_context.correlation_check}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Market Regime:</span>
                  <span className="value">{selectedTrade.enhancement_context.regime}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Volatility Multiplier:</span>
                  <span className="value">{selectedTrade.enhancement_context.volatility_multiplier.toFixed(2)}x</span>
                </div>
                <div className="detail-row">
                  <span className="label">Ensemble Agreement:</span>
                  <span className="value" style={{ color: selectedTrade.enhancement_context.ensemble_agreement > 0.75 ? '#4CAF50' : '#FF9800' }}>
                    {(selectedTrade.enhancement_context.ensemble_agreement * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeAnalysis;
